<script lang="ts">
  import {
    getSwiperContainerStyle,
    styleToString,
  } from "@shared/common/node/style";
  import type {
    ISwiperContainerNode,
    PartialNode,
  } from "@shared/common/node/type";
  import { register } from "swiper/element";
  import { Autoplay } from "swiper/modules";
  import type { SwiperOptions } from "swiper/types";
  import { getCurrentDocumentNode, getStore } from "../../context";
  import Node from "./node.svelte";
  import SwiperSlide from "./swiper-slide.svelte";

  const {
    childrenNodeData,
    swiperRealIndex,
    swiperInstance,
    swiperLength,
    swiperOptions: swiperNodeOptions,
    onSwiperSlide,
    onImpressionEvent,
    rootShown,
    reactionEventListener: dispatchReactionEvent,
  } = getStore();

  const documentNode = getCurrentDocumentNode();

  register();
  let swiperEl: HTMLElement;
  let initRealIndex: number = $swiperRealIndex;
  export let wrapperType: "iframe" | "embedded";
  export let node: PartialNode<ISwiperContainerNode>;
  export let depth: number;

  const swiperOptions = (): SwiperOptions => {
    const options: SwiperOptions = { autoHeight: node.autoHeight };

    options.on = {
      init(swiper) {
        swiperLength.set(swiper.slides.length);
        swiperRealIndex.set(swiper.realIndex);
        swiperInstance.set(swiper);
        swiperNodeOptions.set({
          ready: true,
          preventSlideOnVideoEnd: node.preventSlideOnVideoEnd ?? false,
        });
        // 화면에 보여지기 전에는 슬라이드를 멈춤
        swiper.autoplay?.stop();
        if (node.enabled === false) {
          swiper.disable();
        }
      },
      slideChange(swiper) {
        swiperRealIndex.set(swiper.realIndex);
      },
      realIndexChange(swiper) {
        if (!$childrenNodeData) {
          return;
        }

        const node = $childrenNodeData[swiper.realIndex];

        if (!node?.id) {
          return;
        }

        $onSwiperSlide(node).catch(
          // eslint-disable-next-line no-console
          console.error
        );

        if ($rootShown) {
          $onImpressionEvent(node)
            // eslint-disable-next-line no-console
            .catch(console.error);
        }

        if (swiper.autoplay) {
          swiper.autoplay.start();
        }
      },

      slideNextTransitionStart() {
        dispatchReactionEvent({
          documentNode,
          element: swiperEl,
          node,
          data: {
            nodeId: node.id ?? "",
            nodeName: `${node.name}_NEXT`,
            trigger: { type: "ON_CLICK" },
            action: { type: "SLIDE", direction: "NEXT" },
          },
          // eslint-disable-next-line no-console
        }).catch((e) => console.error(e));
      },
      slidePrevTransitionStart() {
        dispatchReactionEvent({
          documentNode,
          element: swiperEl,
          node,
          data: {
            nodeId: node.id ?? "",
            nodeName: `${node.name}_PREV`,
            trigger: { type: "ON_CLICK" },
            action: { type: "SLIDE", direction: "PREV" },
          },
          // eslint-disable-next-line no-console
        }).catch((e) => console.error(e));
      },
    };

    if (node.speed) {
      options.speed = node.speed;
    }

    if (node.autoplay) {
      options.modules = [Autoplay];
      options.autoplay = {
        delay: node.autoplay.delay,
        disableOnInteraction: node.autoplay.disableOnInteraction,
      };
    }
    if (node.loop) {
      options.loop = node.loop;
    }

    return options;
  };
  $: if (swiperEl) {
    Object.assign(swiperEl, swiperOptions());
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (swiperEl as any).initialize();
  }

  // 화면에 보여짐과 동시에 첫번째 캠페인 대한 IMPRESSION 이벤트 호출
  $: if ($rootShown) {
    if ($childrenNodeData) {
      const firstChildNode = $childrenNodeData[initRealIndex];
      if (firstChildNode && firstChildNode.id) {
        // eslint-disable-next-line no-console
        $onSwiperSlide(firstChildNode).catch(console.error);
        // eslint-disable-next-line no-console
        $onImpressionEvent(firstChildNode).catch(console.error);
      }
    }
  }

  // 화면에 보여지면 슬라이드 시작
  $: if ($rootShown) {
    // TODO: likethix 에 모바일 사이드메뉴에 autoplay sidebar가 있는데,
    // 최초로 보여질 때 transition 관련된 이벤트들이 생기는데, 그 전에
    // autoplay.start()를 호출하면 계속해서 pause 상태로 머물러있는 문제가
    // 발생함. 임시 조치로 setTimeout을 사용함.
    setTimeout(() => $swiperInstance?.autoplay?.start(), 100);
  }
</script>

<div
  style={`${styleToString(getSwiperContainerStyle(node))} z-index: 0;`}
  class="aspect-ratio"
>
  <swiper-container
    bind:this={swiperEl}
    init={false}
    style={node.autoHeight
      ? ""
      : "width: 100%; height: 100%; position:absolute; top:0; left:0;"}
  >
    {#each $childrenNodeData || [] as child, index}
      <SwiperSlide node={child} {index}>
        <Node node={child} {depth} {wrapperType} />
      </SwiperSlide>
    {/each}
    <!-- Safari에서 Swiper안에 있는 video태그 위에 다른 노드들이 안보이는 이슈로 인한 css 처리-->
    <style>
      .swiper-slide-active video,
      .swiper-slide-active video ~ *,
      .swiper-slide-active video ~ * * {
        transform: translateZ(0);
      }
    </style>
  </swiper-container>
</div>

<style>
  .aspect-ratio::before {
    content: "";
    padding-top: var(--cb-swiper-container-aspect-ratio);
    float: left;
  }

  .aspect-ratio::after {
    clear: left;
    content: " ";
    display: table;
  }
</style>
